import {
  CalendarIcon,
  BookOpenIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  Cog6ToothIcon,
  SparklesIcon,
  ClockIcon,
  RectangleStackIcon,
  ChartBarIcon,
  Square3Stack3DIcon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import { TourStep } from './types';

export const tourSteps: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to DayDrive!',
    description: 'Your all-in-one productivity companion. Plan tasks, track learning, take notes, and share your knowledge. Let us show you around!',
    placement: 'center',
    route: '/planner',
    icon: <SparklesIcon className="w-8 h-8 text-indigo-500" />,
  },
  {
    id: 'planner',
    title: 'Planner',
    description: 'Your command center for tasks. View your schedule as a calendar, kanban board, or timeline. Plan your day, week, or month with ease.',
    target: '[data-tour="nav-planner"]',
    placement: 'right',
    route: '/planner',
    icon: <CalendarIcon className="w-6 h-6 text-blue-500" />,
  },
  {
    id: 'calendar-views',
    title: 'Multiple Views',
    description: 'Switch between Overview, Kanban, Timeline and Calendar views. Each view helps you organize your work differently.',
    target: '[data-tour="calendar-views"]',
    placement: 'bottom',
    route: '/planner',
    icon: <RectangleStackIcon className="w-6 h-6 text-green-500" />,
  },
  {
    id: 'add-task',
    title: 'Quick Add Tasks',
    description: 'Add tasks quickly using the input or click the button to open a detailed modal. Set time, duration, priority, and more.',
    target: '[data-tour="add-task"]',
    placement: 'bottom',
    route: '/planner',
    icon: <ClockIcon className="w-6 h-6 text-purple-500" />,
    action: () => {
      // Click the Overview tab to ensure Add Task button is visible
      const viewTabs = document.querySelector('[data-tour="calendar-views"]');
      if (viewTabs) {
        const buttons = viewTabs.querySelectorAll('button');
        // Overview is the 1st button (index 0)
        const overviewBtn = buttons[0] as HTMLButtonElement;
        if (overviewBtn) overviewBtn.click();
      }
    },
  },
  {
    id: 'routines',
    title: 'Routines',
    description: 'Set up recurring time blocks for your regular activities like "Deep Work", "Exercise", or "Learning". They show as colored backgrounds on your calendar. Find them in the sidebar on the right.',
    target: '[data-tour="routines"]',
    placement: 'left',
    route: '/planner',
    icon: <ChartBarIcon className="w-6 h-6 text-orange-500" />,
    action: () => {
      // Click the Calendar tab to show routines sidebar
      const viewTabs = document.querySelector('[data-tour="calendar-views"]');
      if (viewTabs) {
        const buttons = viewTabs.querySelectorAll('button');
        // Calendar is the 4th button (index 3)
        const calendarBtn = buttons[3] as HTMLButtonElement;
        if (calendarBtn) calendarBtn.click();
      }
    },
  },
  {
    id: 'tracks',
    title: 'Learning Tracks',
    description: 'Organize your learning journey into tracks. Create structured paths for courses, tutorials, or any skill you want to master. There are two types of tracks to choose from.',
    placement: 'center',
    route: '/tracks',
    icon: <BookOpenIcon className="w-6 h-6 text-emerald-500" />,
  },
  {
    id: 'tracks-hierarchical',
    title: 'Hierarchical Tracks',
    description: 'Perfect for courses, books, or complex projects. Organize content in 4 levels: Topic → Chapter → Section → Task. Great for structured learning paths with nested content.',
    target: '[data-tour="track-hierarchical"]',
    placement: 'right',
    route: '/tracks',
    spotlightPadding: 12,
    icon: <Square3Stack3DIcon className="w-6 h-6 text-emerald-500" />,
  },
  {
    id: 'tracks-linear',
    title: 'Linear Tracks',
    description: 'Best for simple checklists, habits, or sequential tasks. A flat list of items you can check off one by one. Perfect for daily routines or straightforward task lists.',
    target: '[data-tour="track-linear"]',
    placement: 'right',
    route: '/tracks',
    spotlightPadding: 12,
    icon: <ListBulletIcon className="w-6 h-6 text-amber-500" />,
  },
  {
    id: 'notes',
    title: 'Notes',
    description: 'Capture your thoughts, ideas, and learnings. Write in rich text with a beautiful distraction-free editor. Organize notes in folders.',
    placement: 'center',
    route: '/notes',
    icon: <DocumentTextIcon className="w-6 h-6 text-amber-500" />,
  },
  {
    id: 'blog',
    title: 'Blog',
    description: 'Share your knowledge with the world. Write and publish blog posts. Track your published articles and drafts.',
    placement: 'center',
    route: '/blog',
    icon: <PencilSquareIcon className="w-6 h-6 text-rose-500" />,
  },
  {
    id: 'settings',
    title: 'Settings',
    description: 'Customize DayDrive to fit your workflow. Change themes, configure planner preferences, and manage your account.',
    placement: 'center',
    route: '/settings',
    icon: <Cog6ToothIcon className="w-6 h-6 text-gray-500" />,
  },
  {
    id: 'sidebar-collapse',
    title: 'Collapse Sidebar',
    description: 'Need more space? Collapse the sidebar to focus on your content. The icons remain visible for quick navigation.',
    target: '[data-tour="sidebar-toggle"]',
    placement: 'right',
    route: '/planner',
    icon: <RectangleStackIcon className="w-6 h-6 text-indigo-500" />,
    desktopOnly: true,
  },
  {
    id: 'finish',
    title: "You're All Set!",
    description: "You're ready to boost your productivity! Start by creating your first task or exploring the features. You can always replay this tour from Settings > Data.",
    placement: 'center',
    route: '/planner',
    icon: <SparklesIcon className="w-8 h-8 text-indigo-500" />,
  },
];
